// AI change-detection geospatial blog post — BLOG-004
import { ArrowRight } from "lucide-react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import PostHero from "../../components/PostHero";
import Reveal from "../../components/Reveal";

export default function AiChangeDetectionGeospatial() {
  return (
    <div className="bg-black">
      <Nav route="inner" />

      <PostHero
        category="GIS"
        title="AI change detection at national scale: lessons from a federal pilot"
        author="GIS Practice"
        date="April 2026"
        readTime="7 min read"
        excerpt="A national geospatial agency was reviewing satellite imagery manually across
         a coverage area too large for its analyst team. We built an AI change-detection
         system that gave analysts a queue of high-confidence findings instead of a
         backlog of tiles. Here is what we learned about taking geospatial AI from
         prototype to production."
      />

      <main>
        <div className="max-w-3xl mx-auto px-6 py-12">

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The scale problem in national geospatial monitoring
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              A team of twelve analysts cannot manually review satellite
              imagery covering a national land area on a quarterly cycle. The
              coverage area grows faster than headcount. Imagery resolution keeps
              improving, and the volume of data needing review doubles every two
              to three years. Every government and enterprise GIS lead we work
              with across Australia and NZ describes the same arithmetic — the
              backlog is structural, not a staffing problem you can hire your way
              out of.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The result is prioritisation by gut feel. Analysts review the
              areas they know matter and skip the long tail. Change events in
              low-priority areas go undetected for months. The agency knows this
              is happening and has no systematic way to address it. This is the
              gap <a href="/industries/gis.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">geospatial AI</a> is
              built to close: machines triage the full coverage area, analysts
              spend their judgement where it counts.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What AI change detection does well and where it struggles
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              AI change detection excels at finding pixel-level differences
              between time-series images at scale. A model can review 10,000
              tiles in the time a human analyst reviews ten. For change types
              with clear visual signatures — new structures, cleared vegetation,
              shifts in water extent — detection accuracy is high, and the model
              never gets tired of the long tail.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The hard part is false positives. Seasonal variation, cloud
              shadow, sensor calibration differences and atmospheric effects all
              produce apparent changes that are not real land-cover changes. A
              model that cannot tell these from genuine change will flood
              analysts with noise faster than they can clear it. So the bar for
              a buyable system is not detection alone — it is detection with
              enough precision that analyst time gets more productive, not less.
              That is the question to put to any vendor before you fund a pilot.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              How we built the validation loop
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The key design decision in this engagement was building analyst
              feedback into the system from day one. Every alert the model
              generates gets a disposition from an analyst — confirmed change,
              false positive, or needs review — and that feedback retrains the
              model on a rolling basis. The loop is what removes the classic
              pilot risk: a model that demos well on curated tiles and degrades
              quietly on live imagery.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              In the first month, the false-positive rate was 22%. After three
              months of feedback-driven retraining, it was under 6%. The analysts
              did not experience this as model improvement. They experienced it
              as the system gradually learning what they care about. That
              distinction matters for adoption — and adoption is the metric that
              decides whether an AI transformation programme outlives its pilot
              funding.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The infrastructure decisions that shaped performance
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              We ran inference on-premises inside the agency's existing secure
              environment. That ruled out several cloud-based geospatial AI
              services and required a self-contained pipeline built on
              open-source tooling. The constraint turned out to be an advantage:
              the agency owns the full pipeline and can extend it without vendor
              dependency. It is the same reason
              we <a href="/services/custom-products.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">build custom products</a> that
              hand over source, models and infrastructure — sovereignty
              requirements in government work are not negotiable, so we design
              for them from the first sprint.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Imagery ingestion was standardised to a single internal format
              regardless of source sensor. This added two weeks to the build but
              eliminated a class of errors that had broken an earlier
              proof-of-concept. Data standardisation before model development is
              not optional in multi-sensor geospatial work — budget for it, or
              pay for it twice.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              Results and what they mean for similar programmes
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              At the 12-week mark, analyst throughput had increased by a factor
              of seven, measured by confirmed change events reviewed per analyst
              per week. Precision at 0.9 recall was 99.2%. The agency was
              covering its full national area on a monthly cycle rather than
              quarterly — same headcount, an order of magnitude more ground
              under systematic watch.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The result that surprised us was how quickly analyst confidence
              developed. By week six, analysts were treating the model queue as
              the starting point for their day rather than a secondary tool.
              That workflow shift is what
              our <a href="/services/ai-integration.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI integration practice</a> is
              structured to deliver: not a model on a shelf, but a system
              embedded in the daily process — the difference between a pilot
              that gets renewed and one that gets quietly abandoned.
            </p>
          </Reveal>

        </div>
      </main>

      <section className="bg-black px-6 pb-24 mt-4">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">
            Running change detection over a large coverage area?
          </h3>
          <p className="text-white/60 text-base mt-4">
            We build geospatial AI and change-detection systems for government
            and enterprise teams in Australia and NZ — including secure,
            on-premises environments. Start with a two-week imagery data
            assessment that scopes what is detectable with your archive today.
          </p>
          <a
            href="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3 bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Book an imagery assessment <ArrowRight size={16} />
          </a>
          <div>
            <a href="/industries/gis.html" className="mt-4 inline-flex items-center gap-2 liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors">
              Geospatial AI for GIS teams <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
